import { useState } from 'react'
import { useMutation, useQueryClient } from 'react-query'
import { AvatarBoError } from '../../shared/AvatarBoClient'
import type {
  AmendOptionInput,
  AvatarBoClient,
  BoCatalog,
  OptionRef,
  SwatchRef,
} from '../../shared/types'
import { avatarBoKeys } from './useAvatarCatalogue'

import type { SortUpdate } from '../../shared/reorder-plan'

export interface ReorderVars {
  category: string
  updates: SortUpdate[]
}

export interface UseOptionMutationsOpts {
  client: AvatarBoClient
  setId?: string
  onChanged?: () => void
}

export function useOptionMutations({
  client,
  setId,
  onChanged,
}: UseOptionMutationsOpts) {
  const qc = useQueryClient()
  const [error, setError] = useState<string | undefined>(undefined)
  const key = avatarBoKeys.catalogue(setId ?? '')

  const settled = () => {
    if (setId) {
      qc.invalidateQueries(key)
    }
    qc.invalidateQueries(avatarBoKeys.sets())
    onChanged?.()
  }

  const fail = (e: unknown) =>
    setError(
      e instanceof AvatarBoError ? (e.issues[0]?.detail ?? e.code) : String(e),
    )

  const retire = useMutation((ref: OptionRef) => client.retireOption(ref), {
    onMutate: () => setError(undefined),
    onError: fail,
    onSuccess: settled,
  })

  const restore = useMutation((ref: OptionRef) => client.restoreOption(ref), {
    onMutate: () => setError(undefined),
    onError: fail,
    onSuccess: settled,
  })

  // The colour pair. Not optimistic, like their option siblings: a retirement changes what the
  // rest of the palette means (the tier gate counts ACTIVE swatches), so the server answers first.
  const retireSwatch = useMutation(
    (ref: SwatchRef) => client.retireSwatch(ref),
    {
      onMutate: () => setError(undefined),
      onError: fail,
      onSuccess: settled,
    },
  )

  const restoreSwatch = useMutation(
    (ref: SwatchRef) => client.restoreSwatch(ref),
    {
      onMutate: () => setError(undefined),
      onError: fail,
      onSuccess: settled,
    },
  )

  /**
   * The ONLY optimistic mutation in the module: metadata is one row's own fields, so a rollback is
   * exact. Retire, restore and reorder change what other rows mean and wait for the server.
   */
  const amend = useMutation(
    (input: AmendOptionInput) => client.amendOption(input),
    {
      onMutate: async (input) => {
        setError(undefined)
        if (!setId) {
          return { previous: undefined }
        }
        await qc.cancelQueries(key)
        const previous = qc.getQueryData<BoCatalog>(key)
        qc.setQueryData<BoCatalog | undefined>(key, (current) =>
          current
            ? {
                ...current,
                options: current.options.map((o) =>
                  o.slug === input.slug && o.category === input.category
                    ? {
                        ...o,
                        displayName: input.displayName ?? o.displayName,
                        sortOrder: input.sortOrder ?? o.sortOrder,
                        visibility: input.visibility ?? o.visibility,
                      }
                    : o,
                ),
              }
            : current,
        )

        return { previous }
      },
      onError: (e, _input, context) => {
        const ctx = context as { previous?: BoCatalog } | undefined
        if (ctx?.previous && setId) {
          qc.setQueryData(key, ctx.previous)
        }
        fail(e)
      },
      onSettled: settled,
    },
  )

  const reorder = useMutation(
    async ({ category, updates }: ReorderVars) => {
      // Sequential, not parallel: sort_order is positional, so two concurrent PATCHes on the same
      // section can interleave into an order neither caller asked for.
      for (const update of updates) {
        await client.amendOption({
          setId: setId as string,
          category,
          slug: update.slug,
          sortOrder: update.sortOrder,
        })
      }
    },
    { onMutate: () => setError(undefined), onError: fail, onSuccess: settled },
  )

  return {
    retire,
    restore,
    retireSwatch,
    restoreSwatch,
    amend,
    reorder,
    error,
    clearError: () => setError(undefined),
  }
}
